import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppRoutes, INTEGRATION_CALLS_MODAL_WIDTH, UPLOAD_MEDIA_TYPE_DETAILS } from '../../../constants';
import { AITwinCreationStatus, useAppSelector } from '../../../hooks';
import { Permissions, ProspectPageTabs, UploadMediaType } from '../../../types';
import { conditionalObject } from '../../../utils';
import { Dialog, Icons } from '../../shared';
import CreateSimulationStep from './CreateSimulationStep';
import CreateSimulationTypeStep from './CreateSimulationTypeStep';

const AI_TWIN_TYPE_MODAL_WIDTH = 900;

export enum CreateSimulationStepEnum {
  CREATE_SIMULATION_TYPE_STEP = 'CREATE_SIMULATION_TYPE_STEP',
  CREATE_SIMULATION_STEP = 'CREATE_SIMULATION_STEP',
}

export enum CreateSimulationEnum {
  TEMPLATE = 'TEMPLATE',
  CUSTOM = 'CUSTOM',
  AI_TWIN = 'AI_TWIN',
  PROMPT_TEMPLATE = 'PROMPT_TEMPLATE',
}

interface CreateSimulationModalProps {
  setVisitedTabs: Dispatch<SetStateAction<Set<ProspectPageTabs>>>;
}

const CreateSimulationModal = ({ setVisitedTabs }: CreateSimulationModalProps) => {
  const navigate = useNavigate();

  const { user, organization } = useAppSelector((state) => state.auth);

  const permissions = user?.permissions || [];

  const isSuperAdmin = permissions.includes(Permissions.ADMIN_ACCESS);

  // The status of the AITwin creation process.
  const [aiTwinCreationStatus, setAiTwinCreationStatus] = useState<AITwinCreationStatus | undefined>(undefined);

  // This state is initialized here so it would persist between steps if the user decides to go back.
  const [selectedCreateSimulationType, setSelectedCreateSimulationType] = useState(CreateSimulationEnum.CUSTOM);
  const [targetOrgId, setTargetOrgId] = useState(isSuperAdmin ? undefined : organization?.id);

  const [currentStep, setCurrentStep] = useState(CreateSimulationStepEnum.CREATE_SIMULATION_TYPE_STEP);
  const [mediaType, setMediaType] = useState<UploadMediaType | undefined>();

  // Exits designer mode by navigating to the practice page,
  // since the designer modal only opens in the /prospect page.
  const onCancel = useCallback(() => {
    navigate(AppRoutes.SIMULATIONS);
  }, [navigate]);

  const getModalTitle = useCallback(() => {
    if (aiTwinCreationStatus) {
      return 'Processing media';
    }

    switch (currentStep) {
      case CreateSimulationStepEnum.CREATE_SIMULATION_TYPE_STEP:
        return 'Create simulation';
      case CreateSimulationStepEnum.CREATE_SIMULATION_STEP:
        switch (selectedCreateSimulationType) {
          case CreateSimulationEnum.CUSTOM:
            return 'Create new simulation';
          case CreateSimulationEnum.TEMPLATE:
            return 'Start from existing simulation';
          case CreateSimulationEnum.AI_TWIN:
            return mediaType ? UPLOAD_MEDIA_TYPE_DETAILS[mediaType].label : 'Create AI twin';

          default:
            return undefined;
        }
      default:
        return undefined;
    }
  }, [aiTwinCreationStatus, currentStep, mediaType, selectedCreateSimulationType]);

  const { service: integrationService, icon: integrationIcon } = mediaType
    ? UPLOAD_MEDIA_TYPE_DETAILS[mediaType]
    : { service: undefined, icon: undefined };

  const getModalSize = useCallback(() => {
    // Select integration call step
    if (integrationService && !aiTwinCreationStatus) return INTEGRATION_CALLS_MODAL_WIDTH;

    // Create AI twin step
    if (
      currentStep === CreateSimulationStepEnum.CREATE_SIMULATION_STEP &&
      selectedCreateSimulationType === CreateSimulationEnum.AI_TWIN &&
      !mediaType &&
      !aiTwinCreationStatus
    )
      return AI_TWIN_TYPE_MODAL_WIDTH;
  }, [aiTwinCreationStatus, currentStep, integrationService, mediaType, selectedCreateSimulationType]);

  const integrationCallsModalProps = {
    icon: integrationIcon && <Icons icon={integrationIcon} color="text-transparent" preserveSize />,
    className: 'h-full',
  };

  return (
    <Dialog
      isOpen
      title={getModalTitle()}
      size={getModalSize()}
      onClose={onCancel}
      hideCloseButton={!!aiTwinCreationStatus}
      {...conditionalObject(!!integrationService && !aiTwinCreationStatus, integrationCallsModalProps)}
    >
      {currentStep === CreateSimulationStepEnum.CREATE_SIMULATION_TYPE_STEP && (
        <CreateSimulationTypeStep
          isSuperAdmin={isSuperAdmin}
          targetOrgId={targetOrgId}
          selectedCreateSimulationType={selectedCreateSimulationType}
          setSelectedCreateSimulationType={setSelectedCreateSimulationType}
          setTargetOrgId={setTargetOrgId}
          setCurrentStep={setCurrentStep}
          onCancel={onCancel}
        />
      )}
      {currentStep === CreateSimulationStepEnum.CREATE_SIMULATION_STEP && (
        <CreateSimulationStep
          targetOrgId={targetOrgId}
          selectedCreateSimulationType={selectedCreateSimulationType}
          mediaType={mediaType}
          aiTwinCreationStatus={aiTwinCreationStatus}
          setAiTwinCreationStatus={setAiTwinCreationStatus}
          setVisitedTabs={setVisitedTabs}
          setCurrentStep={setCurrentStep}
          setMediaType={setMediaType}
        />
      )}
    </Dialog>
  );
};

export default CreateSimulationModal;
